import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Plus, Printer, RefreshCw, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, ErrorState, LoadingState, Notice, PageHeader, Spinner, StatusBadge } from "@/components/common/States";
import { useAuth, useSchoolCan } from "@/context/AuthContext";
import { api, errorMessage, fetcher } from "@/lib/api";
import { ReadOnlyBanner } from "../SchoolHome";
import { REPORT_STATUS, formatDate } from "@/lib/labels";

const G = "/school/grades";
const R = "/school/reports";
const angka = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const kunci = (mid, cid) => `${mid}|${cid}`;
const lulusBobot = (total) => Math.abs(total - 100) < 0.01;

const useKonteks = () => useQuery({ queryKey: ["grades-context"], queryFn: () => fetcher(`${G}/context`) });

const SemesterPilih = ({ value, onChange, semesters, testId }) => (
  <div className="space-y-1.5">
    <Label>Semester</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-60" data-testid={testId} aria-label="Semester"><SelectValue placeholder="Pilih semester" /></SelectTrigger>
      <SelectContent>
        {(semesters || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.year_name}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Kartu = ({ title, children, testId, aksi }) => (
  <section className="rounded-2xl border bg-card p-5 space-y-4" data-testid={testId}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h3 className="font-bold">{title}</h3>
      {aksi}
    </div>
    {children}
  </section>
);

const Angka = ({ children, label, testId }) => (
  <div className="rounded-xl border bg-card p-4" data-testid={testId}>
    <p className="font-brand text-2xl font-extrabold tabular-nums">{children}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

// Fungsi pembulir, bukan komponen: dipakai di dalam ekspresi JSX untuk nilai yang bisa kosong.
const nilaiTeks = (nilai) => (typeof nilai === "number" ? nilai.toFixed(2) : "—");

const predikatDari = (nilai, tabel) => {
  if (nilai === null || nilai === undefined) return "—";
  const baris = [...tabel].sort((a, b) => b.min - a.min).find((p) => nilai >= p.min);
  return baris ? baris.label : "—";
};

// ---------- Komponen penilaian ----------

const KomponenEditor = ({ plan, planId, assignmentId, semesterId, onSimpan }) => {
  const awal = () => plan.components.map((c) => ({ name: c.name, weight: c.weight, id: c.id || null }));
  const [rows, setRows] = useState(awal);
  const [kkm, setKkm] = useState(plan.kkm);
  const [buka, setBuka] = useState(planId === "baru");
  const [sinkron, setSinkron] = useState(planId);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (sinkron !== planId) {
    setSinkron(planId);
    setRows(awal());
    setKkm(plan.kkm);
    setBuka(planId === "baru");
  }
  const total = rows.reduce((h, r) => h + (Number(r.weight) || 0), 0);

  if (!buka) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground" data-testid="komponen-ringkas">
          {rows.map((r) => `${r.name} (${r.weight})`).join(" · ")} — KKM {kkm}
        </p>
        <Button variant="outline" size="sm" onClick={() => setBuka(true)} data-testid="nilai-edit-komponen">Ubah komponen</Button>
      </div>
    );
  }
  const simpan = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.put(`${G}/plan`, {
        assignment_id: assignmentId, semester_id: semesterId, kkm: Number(kkm),
        components: rows.map((r) => ({ id: r.id, name: r.name.trim(), weight: Number(r.weight) })),
      });
      toast.success(data.message);
      setBuka(planId === "baru");
      onSimpan();
    } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="komponen-form">
      {rows.map((r, i) => (
        <div key={r.id || `baru-${i}`} className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5 grow min-w-[180px]">
            <Label>Komponen {i + 1}</Label>
            <Input value={r.name} placeholder="mis. Harian" data-testid={`komponen-nama-${i}`}
                   onChange={(e) => setRows((h) => h.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
          </div>
          <div className="space-y-1.5 w-28">
            <Label>Bobot %</Label>
            <Input type="number" min="1" max="100" value={r.weight} data-testid={`komponen-bobot-${i}`}
                   onChange={(e) => setRows((h) => h.map((x, j) => (j === i ? { ...x, weight: e.target.value } : x)))} />
          </div>
          {rows.length > 1 && (
            <Button variant="ghost" size="icon" aria-label={`Hapus komponen ${r.name || i + 1}`} data-testid={`komponen-hapus-${i}`}
                    onClick={() => setRows((h) => h.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
          )}
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-3">
        <Button variant="outline" size="sm" onClick={() => setRows((h) => [...h, { name: "", weight: 0, id: null }])} data-testid="komponen-tambah">
          <Plus className="h-4 w-4 mr-1" /> Tambah komponen
        </Button>
        <div className="space-y-1.5 w-32">
          <Label>KKM</Label>
          <Input type="number" min="0" max="100" value={kkm} data-testid="komponen-kkm" onChange={(e) => setKkm(e.target.value)} />
        </div>
        <span className={`text-sm font-semibold ${lulusBobot(total) ? "text-emerald-700" : "text-red-700"}`} data-testid="komponen-total">
          Total bobot {total}
        </span>
        <Button onClick={simpan} disabled={busy || !lulusBobot(total)} data-testid="komponen-simpan">
          {busy ? <Spinner /> : <Check className="h-4 w-4 mr-1" />} Simpan komponen
        </Button>
      </div>
      {err && <Notice tone="danger" testId="komponen-error">{err}</Notice>}
      {planId === "baru" && <p className="text-xs text-muted-foreground">Komponen awal diambil dari pengaturan penilaian sekolah. Bobot harus berjumlah 100.</p>}
    </div>
  );
};

// ---------- Input nilai guru ----------

const InputNilaiTab = () => {
  const qc = useQueryClient();
  const k = useKonteks();
  const [penugasan, setPenugasan] = useState("");
  const [sem, setSem] = useState("");
  const [isi, setIsi] = useState({});
  const [hilang, setHilang] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const q = k.data;
  useEffect(() => {
    if (!q) return;
    if (!q.assignments.some((a) => a.id === penugasan)) setPenugasan(q.assignments[0]?.id || "");
    if (!q.semesters.some((s) => s.id === sem)) setSem(q.semester?.id || q.semesters[0]?.id || "");
  }, [q, penugasan, sem]);
  const muat = useQuery({
    queryKey: ["grades-matrix", penugasan, sem],
    queryFn: () => fetcher(`${G}/matrix?assignment_id=${encodeURIComponent(penugasan)}&semester_id=${encodeURIComponent(sem)}`),
    enabled: !!penugasan && !!sem,
  });
  useEffect(() => { setIsi({}); setHilang([]); setErr(""); }, [penugasan, sem, muat.dataUpdatedAt]);

  if (k.isLoading || !q) return <LoadingState />;
  if (k.error) return <ErrorState error={k.error} onRetry={k.refetch} />;
  if (muat.error) return <ErrorState error={muat.error} onRetry={muat.refetch} />;
  if (!q.assignments.length) {
    return <EmptyState title="Belum ada penugasan mengajar" description="Admin Sekolah menugaskan guru pada mata pelajaran lewat menu Data Akademik — Penugasan Guru." />;
  }
  // Query matriks baru aktif setelah penugasan dan semester terpilih, jadi tunggu datanya sebelum merender grid.
  if (muat.isLoading || !muat.data) return <LoadingState />;
  const m = muat.data;
  const dipilih = q.assignments.find((a) => a.id === penugasan) || {};
  const plan = m.plan || { components: m.settings.components.map((c) => ({ name: c.name, weight: c.weight, id: null })), kkm: m.settings.kkm_default };
  const batas = m.settings.scale_max;
  const nilaiAkhir = (s) => {
    let num = 0, den = 0;
    plan.components.forEach((c) => {
      const v = isi[kunci(s.membership_id, c.id)] ?? s.scores[c.id];
      if (v !== null && v !== undefined) { num += v * c.weight; den += c.weight; }
    });
    return den ? Math.round((num / den) * 100) / 100 : null;
  };
  const ubah = (mid, cid, asal, mentah) => {
    const v = angka(mentah);
    const bersihkan = (h) => h.filter((x) => !(x.membership_id === mid && x.component_id === cid));
    if (v === null) {
      setIsi((h) => { const s = { ...h }; delete s[kunci(mid, cid)]; return s; });
      if (asal !== null && asal !== undefined) setHilang((h) => [...bersihkan(h), { membership_id: mid, component_id: cid, value: null }]);
      else setHilang(bersihkan);
      return;
    }
    setHilang(bersihkan);
    if (v === asal) { setIsi((h) => { const s = { ...h }; delete s[kunci(mid, cid)]; return s; }); return; }
    setIsi((h) => ({ ...h, [kunci(mid, cid)]: v }));
  };
  const jumlah = Object.keys(isi).length + hilang.length;
  const simpan = async () => {
    const salah = Object.values(isi).some((v) => Number.isNaN(v) || v < 0 || v > batas);
    if (salah) { setErr(`Nilai harus antara 0 sampai ${batas}.`); return; }
    setBusy(true); setErr("");
    try {
      const scores = [
        ...Object.entries(isi).map(([key, value]) => {
          const [membership_id, component_id] = key.split("|");
          return { membership_id, component_id, value };
        }),
        ...hilang,
      ];
      const { data } = await api.post(`${G}/entries`, { assignment_id: penugasan, semester_id: sem, scores });
      toast.success(data.message);
      setIsi({}); setHilang([]);
      qc.invalidateQueries({ queryKey: ["grades-matrix"] });
      qc.invalidateQueries({ queryKey: ["grades-recap"] });
      qc.invalidateQueries({ queryKey: ["grades-summary"] });
      qc.invalidateQueries({ queryKey: ["reports-list"] });
    } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <Kartu title="Pilih pelajaran yang diampu" testId="nilai-pilih">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Penugasan</Label>
            <Select value={penugasan} onValueChange={setPenugasan}>
              <SelectTrigger aria-label="Penugasan" data-testid="nilai-assignment"><SelectValue placeholder="Pilih penugasan" /></SelectTrigger>
              <SelectContent>
                {q.assignments.map((a) => <SelectItem key={a.id} value={a.id}>{a.class_name} · {a.subject_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <SemesterPilih value={sem} onChange={setSem} semesters={q.semesters} testId="nilai-semester" />
        </div>
        <p className="text-sm text-muted-foreground">
          Kelas {m.class.name} · {dipilih.subject_name} · guru {dipilih.teacher_name} · tahun ajaran {m.year?.name || "—"}
        </p>
      </Kartu>

      <Kartu title="Komponen penilaian" testId="nilai-komponen">
        {!m.plan && <Notice tone="info" testId="nilai-belum-komponen">Komponen untuk semester ini belum pernah disimpan. Simpan komponen lebih dahulu, lalu isi nilai per siswa.</Notice>}
        <KomponenEditor plan={plan} planId={m.plan ? m.plan.id : "baru"} assignmentId={penugasan} semesterId={sem}
                        onSimpan={() => qc.invalidateQueries({ queryKey: ["grades-matrix"] })} />
      </Kartu>

      <Kartu title={`Nilai ${m.students.length} siswa`} testId="nilai-grid"
             aksi={<Button onClick={simpan} disabled={!jumlah || busy} data-testid="nilai-save">
                     {busy ? <Spinner /> : <Save className="h-4 w-4 mr-1" />} Simpan nilai{jumlah ? ` (${jumlah})` : ""}
                   </Button>}>
        {!m.students.length ? <EmptyState title="Belum ada siswa" description="Kelas ini belum memiliki siswa aktif. Tambahkan siswa pada menu Anggota." /> : (
          <div className="overflow-x-auto rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Siswa</TableHead>
                  {plan.components.map((c) => (
                    <TableHead key={c.id || c.name} className="text-right">{c.name}<span className="block text-xs font-normal text-muted-foreground">bobot {c.weight}</span></TableHead>
                  ))}
                  <TableHead className="text-right">Nilai akhir</TableHead>
                  <TableHead>Predikat</TableHead>
                  <TableHead>KKM {plan.kkm}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {m.students.map((s) => {
                  const akhir = nilaiAkhir(s);
                  return (
                    <TableRow key={s.membership_id} data-testid={`nilai-baris-${s.membership_id}`}>
                      <TableCell className="font-medium">{s.full_name}<span className="block text-xs font-normal text-muted-foreground">{s.nisn || "—"}</span></TableCell>
                      {plan.components.map((c) => {
                        const asal = c.id ? s.scores[c.id] ?? null : null;
                        const x = (c.id && isi[kunci(s.membership_id, c.id)] !== undefined) ? isi[kunci(s.membership_id, c.id)] : asal ?? "";
                        return (
                          <TableCell key={c.id || c.name} className="text-right">
                            <Input type="number" inputMode="decimal" step="0.01" min="0" max={batas} disabled={!c.id} value={x}
                                   aria-label={`Nilai ${s.full_name} — ${c.name}`} className="w-24 ml-auto text-right"
                                   data-testid={`nilai-input-${s.membership_id}-${c.id || c.name}`}
                                   onChange={(e) => c.id && ubah(s.membership_id, c.id, asal, e.target.value)} />
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right font-bold tabular-nums" data-testid={`nilai-akhir-${s.membership_id}`}>{nilaiTeks(akhir)}</TableCell>
                      <TableCell data-testid={`nilai-predikat-${s.membership_id}`}>{predikatDari(akhir, m.settings.predikat)}</TableCell>
                      <TableCell data-testid={`nilai-tuntas-${s.membership_id}`}>{akhir === null ? "—" : akhir >= plan.kkm ? "Tuntas" : "Belum tuntas"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {err && <Notice tone="danger" testId="nilai-error">{err}</Notice>}
        <p className="text-xs text-muted-foreground">Nilai akhir adalah rata-rata berbobot dari komponen yang sudah diisi; komponen kosong tidak ikut membagi. Kosongkan kotak untuk menghapus nilai yang tersimpan.</p>
      </Kartu>
    </div>
  );
};

// ---------- Rekap nilai ----------

const RekapTab = () => {
  const k = useKonteks();
  const [sem, setSem] = useState("");
  const q = k.data;
  useEffect(() => { if (q && !q.semesters.some((s) => s.id === sem)) setSem(q.semester?.id || q.semesters[0]?.id || ""); }, [q, sem]);
  const r = useQuery({ queryKey: ["grades-recap", sem], queryFn: () => fetcher(`${G}/recap?semester_id=${encodeURIComponent(sem)}`), enabled: !!sem });
  if (k.isLoading || !q) return <LoadingState />;
  if (k.error) return <ErrorState error={k.error} onRetry={k.refetch} />;
  if (r.error) return <ErrorState error={r.error} onRetry={r.refetch} />;
  if (!r.data) return <LoadingState />;
  const rows = r.data.rows;
  return (
    <div className="space-y-5">
      <Kartu title="Kelengkapan penilaian" testId="rekap-konteks"
             aksi={<SemesterPilih value={sem} onChange={setSem} semesters={q.semesters} testId="rekap-semester" />}>
        <p className="text-sm text-muted-foreground" data-testid="rekap-info">
          Semester {r.data.semester?.name || "—"} — {r.data.semester?.year_name || ""}. {rows.length} penugasan
          {q.role === "guru" || q.role === "wali_kelas" ? " pada kelas yang Anda ampu." : " di seluruh rombel sekolah ini."}
        </p>
      </Kartu>
      {!rows.length ? <EmptyState title="Belum ada penugasan aktif" description="Tambahkan penugasan guru pada menu Data Akademik agar nilai dapat diisi." /> : (
        <div className="overflow-x-auto rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Kelas</TableHead><TableHead>Mata pelajaran</TableHead><TableHead>Guru</TableHead>
                <TableHead className="text-right">Siswa</TableHead><TableHead className="text-right">Terisi</TableHead>
                <TableHead className="text-right">Lengkap</TableHead><TableHead className="text-right">Rata-rata</TableHead>
                <TableHead className="text-right">KKM</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((x) => (
                <TableRow key={x.assignment_id} data-testid={`rekap-${x.assignment_id}`}>
                  <TableCell className="font-medium">{x.class_name}</TableCell>
                  <TableCell>{x.subject_name}</TableCell>
                  <TableCell className="text-muted-foreground">{x.teacher_name}</TableCell>
                  <TableCell className="text-right tabular-nums">{x.total_siswa}</TableCell>
                  <TableCell className="text-right tabular-nums">{x.terisi}</TableCell>
                  <TableCell className="text-right tabular-nums">{x.lengkap}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{nilaiTeks(x.rata_rata)}</TableCell>
                  <TableCell className="text-right tabular-nums">{x.kkm}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

// ---------- Rapor ----------

const RaporTab = () => {
  const qc = useQueryClient();
  const k = useKonteks();
  const [sem, setSem] = useState("");
  const [kelas, setKelas] = useState("");
  const [terbuka, setTerbuka] = useState("");
  const [konfirmasi, setKonfirmasi] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const q = k.data;
  useEffect(() => {
    if (!q) return;
    if (!q.semesters.some((s) => s.id === sem)) setSem(q.semester?.id || q.semesters[0]?.id || "");
    if (!q.classes.some((c) => c.id === kelas)) setKelas(q.classes[0]?.id || "");
  }, [q, sem, kelas]);
  const daftar = useQuery({
    queryKey: ["reports-list", sem, kelas],
    queryFn: () => fetcher(`${R}/list?semester_id=${encodeURIComponent(sem)}&class_id=${encodeURIComponent(kelas)}`),
    enabled: !!sem && !!kelas,
  });
  const detail = useQuery({ queryKey: ["report-detail", terbuka], queryFn: () => fetcher(`${R}/${terbuka}`), enabled: !!terbuka });
  useEffect(() => { setTerbuka(""); setErr(""); }, [sem, kelas]);

  if (k.isLoading || !q) return <LoadingState />;
  if (k.error) return <ErrorState error={k.error} onRetry={k.refetch} />;
  if (!q.classes.length) {
    return <EmptyState title="Tidak ada rombel yang Anda wali" description="Rapor disusun oleh wali kelas tiap rombel, atau oleh Admin Sekolah dan Kepala Sekolah." />;
  }
  const pilih = q.classes.find((c) => c.id === kelas) || {};
  const jalan = async (fn) => {
    setBusy(true); setErr("");
    try {
      await fn();
      qc.invalidateQueries({ queryKey: ["reports-list"] });
      qc.invalidateQueries({ queryKey: ["report-detail"] });
      qc.invalidateQueries({ queryKey: ["grades-summary"] });
    } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <Kartu title="Susun dan terbitkan rapor" testId="rapor-konteks">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
          <SemesterPilih value={sem} onChange={setSem} semesters={q.semesters} testId="rapor-semester" />
          <div className="space-y-1.5">
            <Label>Kelas</Label>
            <Select value={kelas} onValueChange={setKelas}>
              <SelectTrigger aria-label="Kelas" data-testid="rapor-kelas"><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
              <SelectContent>{q.classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.student_count} siswa</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4 rounded border" checked={konfirmasi} data-testid="rapor-konfirmasi-kelas"
                   onChange={(e) => setKonfirmasi(e.target.checked)} />
            Terbitkan walau nilai belum lengkap
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={!kelas || busy} data-testid="rapor-susun"
                  onClick={() => jalan(() => api.post(`${R}/draft`, { class_id: kelas, semester_id: sem }).then((res) => toast.success(res.data.message)))}>
            {busy ? <Spinner /> : <RefreshCw className="h-4 w-4 mr-1" />} Susun draf rapor
          </Button>
          <Button disabled={!kelas || busy} data-testid="rapor-terbitkan-kelas"
                  onClick={() => jalan(() => api.post(`${R}/publish-class`, { class_id: kelas, semester_id: sem, konfirmasi_belum_lengkap: konfirmasi }).then((res) => toast.success(res.data.message)))}>
            <Check className="h-4 w-4 mr-1" /> Terbitkan sekelas
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">Kelas {pilih.name || "—"} · {pilih.student_count || 0} siswa. Draf dihitung ulang dari nilai guru; rapor yang sudah terbit tidak ditimpa.</p>
        {err && <Notice tone="danger" testId="rapor-error">{err}</Notice>}
      </Kartu>

      {daftar.isLoading && <LoadingState rows={2} />}
      {daftar.error && <ErrorState error={daftar.error} onRetry={daftar.refetch} />}
      {daftar.data && !daftar.data.rows.length && (
        <EmptyState title="Belum ada rapor" description="Klik Susun draf rapor untuk menghitung rapor semester ini dari nilai yang sudah diinput guru." />
      )}
      {daftar.data && daftar.data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Siswa</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Rata-rata</TableHead>
                <TableHead>Predikat</TableHead><TableHead>Nilai belum lengkap</TableHead><TableHead className="text-right">Terbit sejak</TableHead><TableHead /></TableRow>
            </TableHeader>
            <TableBody>
              {daftar.data.rows.map((x) => (
                <TableRow key={x.id} data-testid={`rapor-baris-${x.id}`}>
                  <TableCell className="font-medium">{x.student_name}<span className="block text-xs font-normal text-muted-foreground">{x.class_name}</span></TableCell>
                  <TableCell><StatusBadge map={REPORT_STATUS} value={x.status} testId={`rapor-status-${x.id}`} /></TableCell>
                  <TableCell className="text-right tabular-nums">{nilaiTeks(x.average)}</TableCell>
                  <TableCell>{x.predikat || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(x.incomplete_subjects || []).length ? x.incomplete_subjects.join(", ") : "lengkap"}</TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">{x.published_at ? formatDate(x.published_at) : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setTerbuka(terbuka === x.id ? "" : x.id)} data-testid={`rapor-buka-${x.id}`}>
                      <Printer className="h-4 w-4 mr-1" /> {terbuka === x.id ? "Tutup" : "Rinci"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {terbuka && (detail.isLoading ? <LoadingState rows={3} /> : detail.error ? <ErrorState error={detail.error} onRetry={detail.refetch} /> : (
        <RaporDetail rapor={detail.data} onTutup={() => setTerbuka("")} />
      ))}
    </div>
  );
};

const RaporDetail = ({ rapor, onTutup }) => {
  const qc = useQueryClient();
  const [catatan, setCatatan] = useState(rapor.catatan_wali || "");
  const [deskripsi, setDeskripsi] = useState({});
  const [konfirmasi, setKonfirmasi] = useState(false);
  const [alasan, setAlasan] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Sengaja hanya bergantung pada rapor.id: nilai awal dibaca ulang saat berpindah rapor, bukan saat server memperbarui baris yang sama.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setCatatan(rapor.catatan_wali || ""); setDeskripsi({}); setKonfirmasi(false); setErr(""); }, [rapor.id]);
  const terbit = rapor.status === "published";
  const jalan = async (fn) => {
    setBusy(true); setErr("");
    try {
      await fn();
      qc.invalidateQueries({ queryKey: ["reports-list"] });
      qc.invalidateQueries({ queryKey: ["report-detail"] });
      qc.invalidateQueries({ queryKey: ["grades-summary"] });
    } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
  };
  const simpan = () => jalan(() => api.patch(`${R}/${rapor.id}`, {
    catatan_wali: catatan,
    deskripsi: Object.entries(deskripsi).map(([subject_id, d]) => ({ subject_id, deskripsi: d })),
  }).then((res) => toast.success(res.data.message)));

  return (
    <Kartu title={`Rapor ${rapor.student_name}`} testId="rapor-detail"
           aksi={<div className="flex items-center gap-2">
                   <StatusBadge map={REPORT_STATUS} value={rapor.status} testId="rapor-detail-status" />
                   <Button variant="ghost" size="sm" onClick={onTutup} data-testid="rapor-tutup">Tutup</Button>
                 </div>}>
      <div className="grid sm:grid-cols-3 gap-4 text-sm">
        <div><p className="text-xs text-muted-foreground">Kelas & semester</p><p className="font-semibold">{rapor.class_name} · {rapor.semester_name} {rapor.year_name}</p></div>
        <div><p className="text-xs text-muted-foreground">NISN</p><p className="font-semibold">{rapor.nisn || "—"}</p></div>
        <div><p className="text-xs text-muted-foreground">Rata-rata nilai</p><p className="font-semibold" data-testid="rapor-rata">{nilaiTeks(rapor.average)} ({rapor.predikat || "—"})</p></div>
      </div>
      <div className="overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Mata pelajaran</TableHead><TableHead>Guru</TableHead><TableHead className="text-right">Nilai</TableHead>
              <TableHead>Predikat</TableHead><TableHead className="text-right">KKM</TableHead><TableHead className="min-w-[260px]">Deskripsi capaian</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {rapor.subjects.map((s) => (
              <TableRow key={s.subject_id} data-testid={`rapor-mapel-${s.subject_id}`}>
                <TableCell className="font-medium">{s.subject_name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{s.teacher_name}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{nilaiTeks(s.value)}</TableCell>
                <TableCell>{s.predikat || "—"}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{s.kkm}</TableCell>
                <TableCell>
                  {terbit ? <p className="text-xs text-muted-foreground">{s.deskripsi}</p> : (
                    <textarea rows="2" className="w-full rounded-md border bg-transparent p-2 text-sm"
                              value={deskripsi[s.subject_id] !== undefined ? deskripsi[s.subject_id] : s.deskripsi}
                              aria-label={`Deskripsi ${s.subject_name}`} data-testid={`rapor-deskripsi-${s.subject_id}`}
                              onChange={(e) => setDeskripsi((h) => ({ ...h, [s.subject_id]: e.target.value }))} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-wrap gap-2 text-sm" data-testid="rapor-hadir">
        {["hadir", "izin", "sakit", "alpha"].map((k) => (
          <span key={k} className="rounded-full border px-3 py-1 font-semibold capitalize">{k} {rapor.attendance[k]}</span>
        ))}
        <span className="rounded-full border px-3 py-1 text-muted-foreground">total hari tercatat {rapor.attendance.total}</span>
      </div>
      {!terbit && (
        <div className="space-y-1.5">
          <Label>Catatan wali kelas</Label>
          <textarea rows="2" className="w-full rounded-md border bg-transparent p-2 text-sm" value={catatan}
                    placeholder="mis. Ananda aktif dalam kegiatan kelompok." data-testid="rapor-catatan"
                    onChange={(e) => setCatatan(e.target.value)} />
        </div>
      )}
      {err && <Notice tone="danger" testId="rapor-detail-error">{err}</Notice>}
      <div className="flex flex-wrap items-end gap-3">
        {!terbit && <Button variant="outline" onClick={simpan} disabled={busy} data-testid="rapor-simpan"><Save className="h-4 w-4 mr-1" /> Simpan perubahan</Button>}
        {!terbit && (
          <>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4 rounded border" checked={konfirmasi} data-testid="rapor-konfirmasi"
                     onChange={(e) => setKonfirmasi(e.target.checked)} />
              Tetap terbitkan walau ada nilai belum lengkap
            </label>
            <Button onClick={() => jalan(() => api.post(`${R}/${rapor.id}/publish`, { konfirmasi_belum_lengkap: konfirmasi }).then((res) => toast.success(res.data.message)))}
                    disabled={busy} data-testid="rapor-terbitkan"><Check className="h-4 w-4 mr-1" /> Terbitkan rapor</Button>
          </>
        )}
        {terbit && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5 w-64">
              <Label>Alasan menarik penerbitan</Label>
              <Input value={alasan} placeholder="mis. ada koreksi nilai" data-testid="rapor-alasan" onChange={(e) => setAlasan(e.target.value)} />
            </div>
            <Button variant="destructive" disabled={busy || alasan.trim().length < 5} data-testid="rapor-tarik"
                    onClick={() => jalan(() => api.post(`${R}/${rapor.id}/unpublish`, { reason: alasan.trim() }).then((res) => toast.success(res.data.message)))}>
              Tarik penerbitan
            </Button>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {terbit ? `Terbit sejak ${formatDate(rapor.published_at)} oleh ${rapor.published_by}. Siswa dan orang tua/wali terverifikasi sudah dapat membacanya.`
                 : "Rapor draf hanya terlihat oleh wali kelas dan pengurus sekolah."}
      </p>
    </Kartu>
  );
};

// ---------- Pengaturan penilaian ----------

const PengaturanTab = () => {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["grades-settings"], queryFn: () => fetcher(`${G}/settings`) });
  const [form, setForm] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.data && !form) setForm({ scale_max: q.data.scale_max, kkm_default: q.data.kkm_default,
                                    predikat: q.data.predikat, components: q.data.components });
  }, [q.data, form]);
  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  if (!form) return null;
  const total = form.components.reduce((h, c) => h + (Number(c.weight) || 0), 0);
  const rubah = (key, i, field, v) => setForm((h) => ({ ...h, [key]: h[key].map((x, j) => (j === i ? { ...x, [field]: v } : x)) }));
  const simpan = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`${G}/settings`, {
        scale_max: Number(form.scale_max), kkm_default: Number(form.kkm_default),
        predikat: form.predikat.map((p) => ({ min: Number(p.min), label: String(p.label).toUpperCase() })),
        components: form.components.map((c) => ({ name: c.name.trim(), weight: Number(c.weight) })),
      });
      toast.success("Pengaturan penilaian sekolah disimpan.");
      qc.invalidateQueries({ queryKey: ["grades-settings"] });
      qc.invalidateQueries({ queryKey: ["grades-matrix"] });
    } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Kartu title="Pengaturan penilaian sekolah" testId="pengaturan">
      <p className="text-sm text-muted-foreground">Menjadi nilai awal setiap komponen penilaian baru. Guru dapat mengubahnya per mata pelajaran dan semester.</p>
      <div className="grid sm:grid-cols-2 gap-4 max-w-md">
        <div className="space-y-1.5">
          <Label>Skala nilai maksimum</Label>
          <Input type="number" min="1" max="100" value={form.scale_max} data-testid="set-skala" onChange={(e) => setForm((h) => ({ ...h, scale_max: e.target.value }))} />
        </div>
        <div className="space-y-1.5">
          <Label>KKM bawaan</Label>
          <Input type="number" min="0" max="100" value={form.kkm_default} data-testid="set-kkm" onChange={(e) => setForm((h) => ({ ...h, kkm_default: e.target.value }))} />
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-semibold">Komponen awal penilaian</p>
        {form.components.map((c, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <Input className="grow min-w-[180px]" value={c.name} aria-label={`Komponen ${i + 1}`} data-testid={`set-komponen-${i}`} onChange={(e) => rubah("components", i, "name", e.target.value)} />
            <Input className="w-24" type="number" min="1" max="100" value={c.weight} aria-label={`Bobot komponen ${i + 1}`} data-testid={`set-bobot-${i}`} onChange={(e) => rubah("components", i, "weight", e.target.value)} />
            {form.components.length > 1 && <Button variant="ghost" size="icon" aria-label={`Hapus komponen ${c.name || i + 1}`} data-testid={`set-hapus-${i}`} onClick={() => setForm((h) => ({ ...h, components: h.components.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
        <Button variant="outline" size="sm" data-testid="set-tambah" onClick={() => setForm((h) => ({ ...h, components: [...h.components, { name: "", weight: 0 }] }))}><Plus className="h-4 w-4 mr-1" /> Tambah komponen</Button>
        <span className={`block text-sm font-semibold ${lulusBobot(total) ? "text-emerald-700" : "text-red-700"}`} data-testid="set-total">Total bobot {total}</span>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-semibold">Predikat (urut dari batas tertinggi)</p>
        {form.predikat.map((p, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <Input className="w-24" value={p.label} aria-label={`Nama predikat ${i + 1}`} data-testid={`set-predikat-${i}`} onChange={(e) => rubah("predikat", i, "label", e.target.value)} />
            <span className="text-sm text-muted-foreground">mulai dari nilai</span>
            <Input className="w-24" type="number" min="0" max="100" value={p.min} aria-label={`Batas nilai predikat ${i + 1}`} data-testid={`set-min-${i}`} onChange={(e) => rubah("predikat", i, "min", e.target.value)} />
            {form.predikat.length > 1 && <Button variant="ghost" size="icon" aria-label={`Hapus predikat ${p.label || i + 1}`} data-testid={`set-hapus-predikat-${i}`} onClick={() => setForm((h) => ({ ...h, predikat: h.predikat.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
        <Button variant="outline" size="sm" data-testid="set-tambah-predikat" onClick={() => setForm((h) => ({ ...h, predikat: [...h.predikat, { label: "", min: 0 }] }))}><Plus className="h-4 w-4 mr-1" /> Tambah predikat</Button>
      </div>
      {err && <Notice tone="danger" testId="set-error">{err}</Notice>}
      <Button onClick={simpan} disabled={busy || !lulusBobot(total)} data-testid="set-simpan">{busy ? <Spinner /> : <Save className="h-4 w-4 mr-1" />} Simpan pengaturan</Button>
    </Kartu>
  );
};

// ---------- Rapor saya / anak ----------

const RaporSayaTab = () => {
  const q = useQuery({ queryKey: ["reports-me"], queryFn: () => fetcher(`${R}/me`) });
  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const orang = q.data.people || [];
  const s = q.data.semester;
  return (
    <div className="space-y-5" data-testid="rapor-saya">
      {s && <p className="text-sm text-muted-foreground">Semester {s.name} — {s.start_date} sampai {s.end_date}</p>}
      {!orang.length && <EmptyState title="Belum tertaut siswa" description="Akun Anda belum terhubung sebagai siswa, atau relasi orang tua/wali belum diverifikasi sekolah." />}
      {orang.map((o) => (
        <section key={o.membership_id} className="space-y-3" data-testid={`rapor-orang-${o.membership_id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-bold">{o.full_name}</h2>
            <span className="rounded-full border px-2 py-0.5 text-xs font-semibold text-muted-foreground">{o.label}</span>
          </div>
          {!o.report ? (
            <Notice tone="warn" testId="rapor-belum-terbit">
              {o.draft_waiting ? "Wali kelas sedang menyusun rapor semester ini. Rapor akan muncul di sini setelah diterbitkan."
                                : "Belum ada rapor untuk semester ini. Rapor disusun setelah nilai mata pelajaran diisi guru dan diterbitkan wali kelas."}
            </Notice>
          ) : <RaporTampil rapor={o.report} />}
        </section>
      ))}
    </div>
  );
};

const RaporTampil = ({ rapor }) => (
  <div className="rounded-2xl border bg-card p-5 space-y-4" data-testid="rapor-tampil">
    <div className="grid sm:grid-cols-4 gap-4 text-sm">
      <div><p className="text-xs text-muted-foreground">Kelas</p><p className="font-semibold">{rapor.class_name}</p></div>
      <div><p className="text-xs text-muted-foreground">Semester</p><p className="font-semibold">{rapor.semester_name} {rapor.year_name}</p></div>
      <div><p className="text-xs text-muted-foreground">Rata-rata nilai</p><p className="font-semibold" data-testid="rapor-saya-rata">{nilaiTeks(rapor.average)} ({rapor.predikat || "—"})</p></div>
      <div><p className="text-xs text-muted-foreground">Diterbitkan</p><p className="font-semibold">{formatDate(rapor.published_at)}</p></div>
    </div>
    <div className="overflow-x-auto rounded-xl border">
      <Table>
        <TableHeader><TableRow><TableHead>Mata pelajaran</TableHead><TableHead className="text-right">Nilai</TableHead><TableHead>Predikat</TableHead><TableHead className="min-w-[260px]">Keterangan</TableHead></TableRow></TableHeader>
        <TableBody>
          {rapor.subjects.map((s) => (
            <TableRow key={s.subject_id} data-testid={`rapor-saya-mapel-${s.subject_id}`}>
              <TableCell className="font-medium">{s.subject_name}<span className="block text-xs font-normal text-muted-foreground">{s.teacher_name}</span></TableCell>
              <TableCell className="text-right tabular-nums font-semibold">{nilaiTeks(s.value)}</TableCell>
              <TableCell>{s.predikat || "—"}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{s.deskripsi}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    <div className="flex flex-wrap gap-2 text-sm" data-testid="rapor-saya-hadir">
      {["hadir", "izin", "sakit", "alpha"].map((k) => <span key={k} className="rounded-full border px-3 py-1 font-semibold capitalize">{k} {rapor.attendance[k]}</span>)}
    </div>
    {rapor.catatan_wali && (
      <div className="rounded-xl bg-muted p-4 text-sm" data-testid="rapor-saya-catatan">
        <p className="font-semibold">Catatan wali kelas</p>
        <p className="mt-1">{rapor.catatan_wali}</p>
      </div>
    )}
    {!!rapor.incomplete_subjects?.length && (
      <Notice tone="warn" testId="rapor-saya-belumlengkap">Nilai {rapor.incomplete_subjects.join(", ")} belum diisi guru pada rapor ini.</Notice>
    )}
  </div>
);

export default function GradesPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const dapatBaca = useSchoolCan("grades:read");
  const dapatTulis = useSchoolCan("grades:write");
  const dapatRapor = useSchoolCan("grades:publish");
  const dapatSetel = useSchoolCan("grades:settings");
  const peran = me?.active_school?.role;
  const tabs = [
    ...(dapatTulis ? [["nilai", "Input Nilai", <InputNilaiTab key="nilai" />]] : []),
    ...(dapatBaca ? [["rekap", "Rekap Nilai", <RekapTab key="rekap" />]] : []),
    ...(dapatRapor ? [["rapor", "Rapor", <RaporTab key="rapor" />]] : []),
    ...(dapatSetel ? [["pengaturan", "Pengaturan Penilaian", <PengaturanTab key="pengaturan" />]] : []),
    ...(["siswa", "orang_tua"].includes(peran) ? [["rapor-saya", peran === "orang_tua" ? "Rapor Anak" : "Rapor Saya", <RaporSayaTab key="rapor-saya" />]] : []),
  ];
  const ringkas = useQuery({ queryKey: ["grades-summary"], queryFn: () => fetcher(`${G}/summary`), enabled: dapatBaca });
  const sm = ringkas.data;
  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <PageHeader eyebrow="Penilaian" title="Penilaian & Rapor"
                  description="Guru mengisi nilai per komponen, wali kelas menyusun dan menerbitkan rapor, siswa serta orang tua/wali membaca rapor yang sudah terbit."
                  actions={<Button variant="outline" onClick={() => qc.invalidateQueries()} data-testid="grades-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Segarkan</Button>} />
      <ReadOnlyBanner />
      {sm && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-6" data-testid="grades-dashboard">
          <Angka label={`Penugasan · ${sm.semester?.name || "belum ada semester"}`} testId="grades-sum-penugasan">{sm.penugasan}</Angka>
          <Angka label="Sudah ada nilai" testId="grades-sum-terisi">{sm.penugasan_terisi}</Angka>
          <Angka label="Nilai lengkap" testId="grades-sum-lengkap">{sm.penugasan_lengkap}</Angka>
          <Angka label="Rapor terbit / draf">{sm.rapor.published} / {sm.rapor.draft}</Angka>
        </div>
      )}
      {!tabs.length ? (
        <EmptyState title="Akses belum tersedia" description="Peran Anda belum memiliki izin pada halaman penilaian." />
      ) : (
        <Tabs defaultValue={tabs[0][0]}>
          <TabsList className="flex-wrap h-auto">
            {tabs.map(([k, label]) => <TabsTrigger key={k} value={k} data-testid={`grades-tab-${k}`}>{label}</TabsTrigger>)}
          </TabsList>
          {tabs.map(([k, , el]) => <TabsContent key={k} value={k} className="mt-6">{el}</TabsContent>)}
        </Tabs>
      )}
    </div>
  );
}
